import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Package,
  Trash2,
  Eye,
  Shirt,
  Layers,
  LoaderCircle,
  X,
  CheckCircle2,
  XCircle,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../constant/api";

// =====================================================
// VIEW PRODUCT MODAL COMPONENT
// =====================================================
function ViewGarmentModal({ productId, onClose }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState("");

  useEffect(() => {
    if (productId) {
      fetchProductDetails();
    }
  }, [productId]);

  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/GarmentsProduct/getProduct/${productId}`);
      if (response.data?.success) {
        const data = response.data.data;
        setProduct(data);
        const primary = data.images?.[0]?.image || data.image;
        if (primary) {
          setActiveImage(`http://localhost:4004/${primary}`);
        }
      } else {
        toast.error("Failed to load product details");
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      toast.error("Error fetching details");
    } finally {
      setLoading(false);
    }
  };

  if (!productId) return null;

  const money = (val) =>
    Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-3 backdrop-blur-sm sm:p-6">
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-3xl bg-white shadow-2xl overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <Shirt size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {loading ? "Loading Product..." : product?.product_name}
              </h2>
              <p className="text-xs text-slate-500">
                {product?.product_code ? `Code: ${product.product_code}` : "Product Details"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        {loading ? (
          <div className="flex h-80 items-center justify-center">
            <LoaderCircle size={32} className="animate-spin text-indigo-600" />
          </div>
        ) : product ? (
          <div className="overflow-y-auto p-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Left Column - Main Image & Thumbnails */}
              <div className="space-y-4">
                <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                  {activeImage ? (
                    <img
                      src={activeImage}
                      alt={product.product_name}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/400?text=No+Image";
                      }}
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center text-slate-400">
                      <ImageIcon size={48} />
                      <span className="mt-2 text-xs">No image available</span>
                    </div>
                  )}
                </div>

                {/* Thumbnails */}
                {product.images && product.images.length > 0 && (
                  <div className="flex items-center gap-2 overflow-x-auto pb-1">
                    {product.images.map((img, idx) => {
                      const imgUrl = `http://localhost:4004/${img.image}`;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setActiveImage(imgUrl)}
                          className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition ${
                            activeImage === imgUrl
                              ? "border-indigo-600 ring-2 ring-indigo-100"
                              : "border-slate-200 opacity-70 hover:opacity-100"
                          }`}
                        >
                          <img
                            src={imgUrl}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Right Column - Product Specs */}
              <div className="space-y-5">
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-lg bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-600">
                    {product.product_category || "General"}
                  </span>
                  <span className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    Gender: {product.gender || "Unisex"}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 rounded-lg px-3 py-1 text-xs font-bold ${
                      product.published
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {product.published ? (
                      <>
                        <CheckCircle2 size={13} /> Published
                      </>
                    ) : (
                      <>
                        <XCircle size={13} /> Draft
                      </>
                    )}
                  </span>
                </div>

                {/* Pricing Box */}
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-baseline gap-3">
                    <span className="text-2xl font-extrabold text-slate-900">
                      ₹{money(product.price)}
                    </span>
                    {product.mrp && Number(product.mrp) > Number(product.price) && (
                      <span className="text-sm font-semibold text-slate-400 line-through">
                        MRP ₹{money(product.mrp)}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Per {product.unit || "pcs"} • Inclusive of all taxes
                  </p>
                </div>

                {/* Attributes Grid */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-xl border border-slate-100 p-3">
                    <span className="text-slate-400">Brand</span>
                    <p className="mt-0.5 font-bold text-slate-800">{product.brand || "—"}</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 p-3">
                    <span className="text-slate-400">Fabric</span>
                    <p className="mt-0.5 font-bold text-slate-800">{product.fabric || "—"}</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 p-3">
                    <span className="text-slate-400">Pattern</span>
                    <p className="mt-0.5 font-bold text-slate-800">{product.pattern || "—"}</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 p-3">
                    <span className="text-slate-400">HSN Code</span>
                    <p className="mt-0.5 font-bold text-slate-800">{product.hsn || "—"}</p>
                  </div>
                </div>

                {/* Stock Count */}
                <div className="flex items-center justify-between rounded-xl bg-indigo-600 p-3.5 text-white">
                  <div className="flex items-center gap-2">
                    <Package size={18} />
                    <span className="text-xs font-medium">Total Stock</span>
                  </div>
                  <span className="text-base font-extrabold">{product.total_stock || 0}</span>
                </div>

                {/* Description */}
                {product.description && (
                  <div>
                    <h4 className="text-xs font-bold text-slate-700">Description</h4>
                    <p className="mt-1 text-xs leading-relaxed text-slate-600">
                      {product.description}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Variants Section */}
            <div className="mt-8 border-t border-slate-100 pt-6">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900">
                <Layers size={16} className="text-indigo-600" />
                Product Variants ({product.variants?.length || 0})
              </h3>

              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="p-3 font-semibold">Size</th>
                      <th className="p-3 font-semibold">Color</th>
                      <th className="p-3 font-semibold">SKU</th>
                      <th className="p-3 font-semibold text-right">Quantity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {product.variants && product.variants.length > 0 ? (
                      product.variants.map((v, i) => (
                        <tr key={i} className="hover:bg-slate-50/50">
                          <td className="p-3 font-bold text-slate-900">{v.size}</td>
                          <td className="p-3">{v.color}</td>
                          <td className="p-3 font-mono text-[11px] text-slate-500">{v.sku}</td>
                          <td className="p-3 text-right font-bold text-indigo-600">{v.qty}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="p-4 text-center text-slate-400">
                          No variants recorded.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Barcode & QR Code Section */}
            {(product.barcode || product.qrcode) && (
              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 text-xs font-bold text-slate-700">Scannable Codes</h4>
                <div className="flex flex-wrap items-center justify-around gap-4">
                  {product.barcode && (
                    <div className="text-center">
                      <img
                        src={`http://localhost:4004/${product.barcode}`}
                        alt="Barcode"
                        className="h-16 object-contain"
                      />
                      <span className="mt-1 block text-[10px] font-mono text-slate-500">
                        {product.product_code}
                      </span>
                    </div>
                  )}
                  {product.qrcode && (
                    <div className="text-center">
                      <img
                        src={`http://localhost:4004/${product.qrcode}`}
                        alt="QR Code"
                        className="h-20 w-20 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 text-center text-slate-500">Product details not found.</div>
        )}
      </div>
    </div>
  );
}

// =====================================================
// MAIN PRODUCTS LIST PAGE
// =====================================================
export default function GarmentsList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [viewProductId, setViewProductId] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get("/GarmentsProduct/getProducts");
      if (response.data?.success) {
        setProducts(response.data.data || []);
      } else {
        toast.error("Failed to fetch products");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error loading product list");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      const response = await api.delete(`/GarmentsProduct/deleteProduct/${id}`);
      if (response.data?.success) {
        toast.success("Product deleted successfully");
        setProducts((prev) => prev.filter((p) => p.garment_product_id !== id));
      } else {
        toast.error(response.data?.message || "Failed to delete product");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Server error during deletion");
    }
  };

  const filteredProducts = products.filter((item) => {
    const matchesSearch =
      item.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.product_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.brand?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === "All" || item.product_category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const money = (val) =>
    Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
              <Shirt size={22} />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-slate-900 sm:text-2xl">
                Garments Inventory
              </h1>
              <p className="text-xs text-slate-500 sm:text-sm">
                Manage products, variants, and stock
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate("/AddGarmentsProduct")}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-md shadow-indigo-200 transition hover:bg-indigo-700 active:scale-95"
          >
            <Plus size={18} />
            <span>Add Product</span>
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
        {/* Search & Category Filter Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, code, or brand..."
              className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-3 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
            />
          </div>

          {/* Quick Category Buttons */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
            {["All", "Shirts", "T-Shirts", "Jeans", "Trousers", "Kurtis"].map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-xl px-3.5 py-2 text-xs font-semibold whitespace-nowrap transition ${
                  selectedCategory === cat
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3">
            <LoaderCircle size={32} className="animate-spin text-indigo-600" />
            <p className="text-sm font-medium text-slate-500">Loading garment items...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          /* Empty State */
          <div className="flex h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
            <Package size={40} className="text-slate-300 mb-2" />
            <h3 className="text-base font-bold text-slate-800">No products found</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-sm">
              We couldn't find any garments matching your search. Try adjusting your filters or click Add Product above.
            </p>
          </div>
        ) : (
          /* Responsive Product Card Grid */
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredProducts.map((product) => {
              const primaryImage = product.images?.[0]?.image || product.image;
              const imageUrl = primaryImage
                ? `http://localhost:4004/${primaryImage}`
                : null;

              return (
                <div
                  key={product.garment_product_id}
                  className="group flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-indigo-300 hover:shadow-md"
                >
                  <div>
                    {/* Image Box */}
                    <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-slate-100 border border-slate-100">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={product.product_name}
                          className="h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/300?text=No+Image";
                          }}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-slate-400">
                          <Shirt size={36} />
                        </div>
                      )}

                      {/* Category Badge */}
                      <span className="absolute left-2.5 top-2.5 rounded-lg bg-white/90 backdrop-blur-md px-2.5 py-1 text-[10px] font-bold text-slate-800 shadow-sm">
                        {product.product_category || "General"}
                      </span>

                      {/* Stock Pill */}
                      <span
                        className={`absolute right-2.5 top-2.5 rounded-lg px-2.5 py-1 text-[10px] font-bold shadow-sm ${
                          Number(product.total_stock) > 0
                            ? "bg-emerald-500 text-white"
                            : "bg-red-500 text-white"
                        }`}
                      >
                        Stock: {product.total_stock || 0}
                      </span>
                    </div>

                    {/* Metadata */}
                    <div className="mt-4">
                      <p className="text-[11px] font-semibold text-indigo-600">
                        {product.product_code} {product.brand ? `• ${product.brand}` : ""}
                      </p>
                      <h3 className="mt-1 line-clamp-1 text-sm font-bold text-slate-900" title={product.product_name}>
                        {product.product_name}
                      </h3>

                      <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                        <Layers size={14} className="text-slate-400" />
                        <span>{product.variants?.length || 0} Variant(s)</span>
                      </div>
                    </div>
                  </div>

                  {/* Pricing and Action Buttons */}
                  <div className="mt-5 border-t border-slate-100 pt-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase font-semibold text-slate-400">Price</p>
                        <p className="text-base font-extrabold text-slate-900">
                          ₹{money(product.price)}
                        </p>
                      </div>

                      {product.mrp && Number(product.mrp) > Number(product.price) && (
                        <div className="text-right">
                          <p className="text-[10px] uppercase font-semibold text-slate-400">MRP</p>
                          <p className="text-xs font-semibold text-slate-400 line-through">
                            ₹{money(product.mrp)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setViewProductId(product.garment_product_id)}
                        className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        <Eye size={15} />
                        <span>View</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(product.garment_product_id)}
                        className="flex items-center justify-center gap-1.5 rounded-xl border border-red-100 bg-red-50 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-100"
                      >
                        <Trash2 size={15} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Render View Modal when a product is selected */}
      {viewProductId && (
        <ViewGarmentModal
          productId={viewProductId}
          onClose={() => setViewProductId(null)}
        />
      )}
    </div>
  );
}